import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { norm } from "@/lib/format";
import { Input } from "@/components/ui/input";

/** Selector de estudiante con búsqueda por nombre, matrícula, grado o tutor. */
export const BuscadorEstudiante = ({ estudiantes, valor, onChange, testId = "buscador-estudiante" }) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const caja = useRef(null);

  const elegido = estudiantes.find((e) => e.id === valor) || null;

  useEffect(() => {
    const fuera = (ev) => { if (caja.current && !caja.current.contains(ev.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  const resultados = useMemo(() => {
    const q = norm(texto.trim());
    const lista = q
      ? estudiantes.filter((e) => [e.nombre, e.matricula, e.grado, e.seccion, e.tutor_nombre]
          .filter(Boolean).some((v) => norm(v).includes(q)))
      : estudiantes;
    return lista.slice(0, 40);
  }, [texto, estudiantes]);

  return (
    <div className="relative" ref={caja} data-testid={testId}>
      <button type="button" onClick={() => { setAbierto(!abierto); setTexto(""); }}
              data-testid={`${testId}-abrir`}
              className="flex w-full items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary/50">
        <span className={elegido ? "" : "text-muted-foreground"}>
          {elegido ? (
            <>
              <span className="font-medium">{elegido.nombre}</span>
              <span className="ml-2 font-mono text-xs text-muted-foreground">{elegido.matricula}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {elegido.grado}{elegido.tutor_nombre ? ` · ${elegido.tutor_nombre}` : ""}
              </span>
            </>
          ) : "Busque el estudiante por nombre, matrícula o tutor…"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {abierto && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
          <div className="relative border-b border-border/60 p-2">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={texto} onChange={(e) => setTexto(e.target.value)}
                   data-testid={`${testId}-input`} className="pl-9"
                   placeholder="Nombre, matrícula, grado o tutor…" />
          </div>
          <ul className="max-h-64 overflow-y-auto" data-testid={`${testId}-lista`}>
            {resultados.map((e) => (
              <li key={e.id}>
                <button type="button" data-testid={`opcion-estudiante-${e.matricula}`}
                        onClick={() => { onChange(e.id); setAbierto(false); }}
                        className="flex w-full items-start justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{e.nombre}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {e.matricula} · {e.grado}{e.seccion ? ` ${e.seccion}` : ""}
                      {e.tutor_nombre ? ` · ${e.tutor_nombre}` : ""}
                    </span>
                  </span>
                  {e.id === valor && <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />}
                </button>
              </li>
            ))}
            {resultados.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-muted-foreground"
                  data-testid={`${testId}-vacio`}>
                Ningún estudiante activo coincide con la búsqueda.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
